
import React from 'react';
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import BusinessCard from "@/components/BusinessCard";
import CategoryFilter from "@/components/CategoryFilter";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchBusinesses } from "@/services/businessService";
import { Skeleton } from "@/components/ui/skeleton";
import Footer from "@/components/Footer";
import CoFounderBanner from "@/components/CoFounderBanner";
import { BusinessCategoryString } from '@/types/business';
import { useIsMobile } from '@/hooks/use-mobile';

const Home = () => {
  const [activeCategory, setActiveCategory] = React.useState<BusinessCategoryString | undefined>(undefined);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const { data: businesses, isLoading, error } = useQuery({
    queryKey: ['businesses'],
    queryFn: fetchBusinesses,
  });
  
  const filteredBusinesses = activeCategory && businesses
    ? businesses.filter((business) => business.category === activeCategory)
    : businesses || [];

  const displayBusinesses = filteredBusinesses.slice(0, isMobile ? 3 : 6);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <Hero />
      
      <section id="businesses" className="container py-10 md:py-16 px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Featured Listings</h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Discover sustainable businesses and services making a difference
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button 
              onClick={() => navigate("/businesses")}
              variant="outline"
              className="border-eco-500 text-eco-500 hover:bg-eco-500 hover:text-white"
            >
              View All
            </Button>
          </div>
        </div>
        
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-4">
          <CategoryFilter 
            activeCategory={activeCategory} 
            setActiveCategory={setActiveCategory} 
          />
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {Array.from({ length: isMobile ? 3 : 6 }).map((_, i) => (
              <div key={i} className="rounded-lg overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <div className="p-4">
                  <Skeleton className="h-4 w-1/4 mb-2" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-8 md:py-12">
            <p className="text-lg md:text-xl text-red-500">Error loading businesses. Please try again later.</p>
          </div>
        ) : displayBusinesses.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {displayBusinesses.map((business) => (
              <BusinessCard key={business.id} business={business} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 md:py-12">
            <p className="text-lg md:text-xl text-muted-foreground">No businesses found in this category.</p>
            <Button 
              onClick={() => setActiveCategory(undefined)}
              className="mt-4 bg-eco-500 hover:bg-eco-600"
            >
              View All Categories
            </Button>
          </div>
        )}
      </section>
      
      <CoFounderBanner />
      
      <section className="bg-earth-100 py-10 md:py-16 px-4 md:px-6">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Want to list your eco-friendly business?</h2>
            <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8">
              Join our community of sustainable businesses and reach environmentally conscious customers.
            </p>
            <Button 
              onClick={() => navigate("/submit")}
              className="bg-eco-500 hover:bg-eco-600"
            >
              Add Your Listing
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Home;
